import { DocumentsHttp } from '../http.ts'
import { Document, DocumentName } from '../types'

const PREFIX = '/documents'
const headers = {
  'Content-type': 'application/json'
}

function buildAuthHeaders(accessToken: string) {
  const normalized = accessToken?.replaceAll('"', '').trim()
  if (!normalized) {
    return headers
  }
  return {
    ...headers,
    Authorization: `Bearer ${normalized}`
  }
}

export { resolveDocumentsAccessToken } from '../sso/services/documents-access.service'

export const getCurrentDocuments = (accessToken: string) => {
  return DocumentsHttp.get(`${PREFIX}/documents-data?pageSize=25`, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const getDocumentDataById = (documentId: string, accessToken: string) => {
  return DocumentsHttp.get(`${PREFIX}/document-data/${documentId}`, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const saveDocumentToAnalyze = (payload: Document | Partial<Document>, accessToken: string) => {
  return DocumentsHttp.post(`${PREFIX}/save-payment-document`, { ...payload }, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const getUploadDocumentPreSignedURL = (payload: DocumentName, accessToken: string) => {
  return DocumentsHttp.post(`${PREFIX}/get-upload-document-pre-signed-url`, { ...payload }, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const getBusinessDocumentationPreSignedURL = (
  payload: {
    businessDocumentationFileType: string
    businessDocumentationFileSystemName: string
  },
  accessToken: string
) => {
  return DocumentsHttp.post(`${PREFIX}/get-business-documentation-pre-signed-url`, { ...payload }, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const getDownloadDocumentPreSignedURL = (payload: DocumentName, accessToken: string) => {
  return DocumentsHttp.post(`${PREFIX}/get-download-document-pre-signed-url`, { ...payload }, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const getAttachmentFilePreSignedURL = (
  payload: {
    attachmentFileType: string
    attachmentFileSystemName: string
  },
  accessToken: string
) => {
  return DocumentsHttp.post(`${PREFIX}/get-attachment-file-pre-signed-url`, { ...payload }, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const getDownloadAttachmentFilePreSignedURL = (
  payload: {
    attachmentFilename: string
  },
  accessToken: string
) => {
  return DocumentsHttp.post(`${PREFIX}/get-download-attachment-file-pre-signed-url`, { ...payload }, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const uploadDocumentToS3 = (preSignedURL: string, formData: File) => {
  return DocumentsHttp.put(preSignedURL, formData, {
    headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
  })
}

export const uploadBusinessDocumentationDocumentToS3 = (preSignedURL: string, documentType: string, formData: File) => {
  return DocumentsHttp.put(preSignedURL, formData, {
    headers: { 'Content-Type': documentType }
  })
}

export const uploadAttachmentFileToS3 = (preSignedURL: string, fileType: string, formData: File) => {
  return DocumentsHttp.put(preSignedURL, formData, {
    headers: { 'Content-Type': fileType }
  })
}

export const deleteDocumentData = (documentId: string, accessToken: string) => {
  return DocumentsHttp.put(`${PREFIX}/delete-document/${documentId}`, {}, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const updateDocumentData = (documentId: string, documentDataToUpdate: Partial<Document>, accessToken: string) => {
  return DocumentsHttp.put(`${PREFIX}/update-document/${documentId}`, { ...documentDataToUpdate }, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const downloadProcessedDocument = (documentId: string, accessToken: string) => {
  return DocumentsHttp.get(`${PREFIX}/download-processed-document/${documentId}`, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const downloadCompletedDocument = (documentId: string, accessToken: string) => {
  return DocumentsHttp.get(`${PREFIX}/download-completed-document/${documentId}`, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const downloadProofOfOperationPayments = (documentId: string, accessToken: string) => {
  return DocumentsHttp.get(`${PREFIX}/download-proof-of-operation-payments/${documentId}`, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const downloadProofOfOperationPaymentsCSV = (documentId: string, accessToken: string) => {
  return DocumentsHttp.get(`${PREFIX}/download-proof-of-operation-payments-csv/${documentId}`, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const getCryptoWalletData = (accessToken: string, selectedNetwork: string) => {
  return DocumentsHttp.get(`${PREFIX}/crypto-wallet-data/${selectedNetwork}`, {
    headers: buildAuthHeaders(accessToken)
  })
}

export const sendCryptoFunds = (payload: object, accessToken: string) => {
  return DocumentsHttp.post(`${PREFIX}/send-crypto-funds`, payload, {
    headers: buildAuthHeaders(accessToken)
  })
}
